import { useState, useEffect } from "react"
import Grid from "@mui/material/Grid"
import MDBox from "components/MDBox"
import DashboardLayout from "../components/DashboardLayout"
import DashboardNavbar from "../components/DashboardNavbar"
import HeadingInfoCard from "../components/HeadingInfoCard"
import ResidentCardList, { ResidentCard } from "./components/ResidentCardList"
import FacilityInfo, { FacilityInfoProps } from "./components/FacilityInfo"

import { useParams } from "react-router-dom"
import { useNavigate } from "react-router-dom"
import { useLoading } from "../LoadingContext"
import { getSummary } from "../../api/summary"
import { getFacility } from "../../api/facility"
import { getMedicalRecordList } from "../../api/medical_record_list"
import { ManageDeviceData } from "../manage/device/"
import { useConnectionError } from "../ConnectionErrorContext"

const mockHeadingInfoData = {
  residentCount: " 戸",
  residentTotal: "総戸数 戸中",
  detectionCount: " 件",
  detectionPeriod: "過去30日間",
  alertCount: " 件",
  alertPeriod: "過去30日間"
}

function Facility() {
  const { startLoading, stopLoading } = useLoading()
  const { showConnectionError } = useConnectionError()

  const [facilityData, setFacilityData] = useState<FacilityInfoProps | null>(null)
  const [headingInfoData, setHeadingInfoData] = useState(mockHeadingInfoData)
  const [residentData, setResidentData] = useState<ResidentCard[]>([])
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const [devices, setDevices] = useState<ManageDeviceData[]>([]) // デバイス一覧の状態
  const [devicePlaces, setDevicePlaces] = useState<string[]>([]) // デバイス設置場所の状態
  const [filter, setFilter] = useState<number>(0)

  if (localStorage.getItem("session_code") == "") {
    navigate("/login")
    return <></>
  }

  const handleSummaryClick = (type: number) => {
    setFilter(type) // クリックされたSummaryに応じてフィルタを更新
  }

  const fetchSummary = async () => {
    try {
      startLoading()
      if (id) {
        const summaryData = await getSummary(1, id)
        setHeadingInfoData({
          residentCount: summaryData.medical_record_count + "戸",
          residentTotal: "",
          detectionCount: summaryData.detection_count + "件",
          detectionPeriod: "",
          alertCount: summaryData.alert_count + "件",
          alertPeriod: ""
        })
      }
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        navigate("/login")
      } else if (!error.response) {
        showConnectionError(true)
      }
    } finally {
      stopLoading()
    }
  }

  const fetchFacility = async () => {
    try {
      startLoading()
      if (id) {
        const facilityData = await getFacility(id)
        //console.log(facilityData)
        setFacilityData({
          facilityId: facilityData.facility_id,
          facilityName: facilityData.name,
          address: facilityData.address,
          phoneNumber: facilityData.phone,
          buildingType: facilityData.building_type,
          catvName: facilityData.catv_name
        })
      }
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        navigate("/login")
      } else if (!error.response) {
        showConnectionError(true)
      }
    } finally {
      stopLoading()
    }
  }

  const fetchInfo = async () => {
    try {
      startLoading()
      if (id) {
        const medicalRecordData = await getMedicalRecordList(id, 0)
        //console.log("建物データ")
        //console.log(medicalRecordData)

        // 現在時刻（秒単位のUNIXタイムスタンプ）
        const now = Math.floor(Date.now() / 1000)
        // 30日間の秒数 (30日 * 24時間 * 60分 * 60秒)
        const POC_LIMIT_SECONDS = 30 * 24 * 60 * 60

        setResidentData(() => [
          ...medicalRecordData.medical_record_list.map((record: any) => {
            const isExpired =
              record.is_poc === 1 && record.poc_start_date > 0 && now - record.poc_start_date >= POC_LIMIT_SECONDS
            return {
              id: record.id,
              name: record.name,
              initial: record.initial,
              status: isExpired
                ? "poc"
                : record.device_count === 0
                  ? "unset"
                  : record.device_state !== 1
                    ? "alert"
                    : record.detection !== 1
                      ? "warning"
                      : "success", // 型アサーション
              room_no: record.room_no,
              log_date: record.log_date,
              detection: record.detection,
              device_state: record.device_state,
              device_count: record.device_count,
              stop_mail: record.stop_mail
            }
          })
        ])

        // デバイス一覧の設定
        setDevices(medicalRecordData.devices)

        // デバイス設置場所の設定
        setDevicePlaces(medicalRecordData.device_places)
      }
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        navigate("/login")
      } else if (!error.response) {
        showConnectionError(true)
      }
    } finally {
      stopLoading()
    }
  }

  useEffect(() => {
    fetchSummary()
    fetchFacility()
    fetchInfo()
  }, [id])

  const handleReload = async () => {
    await fetchInfo()
  }

  return (
    <DashboardLayout>
      <MDBox>
        <DashboardNavbar absolute={true} />
      </MDBox>
      <MDBox mt={6}>{facilityData && <FacilityInfo {...facilityData} />}</MDBox>
      <MDBox mt={4}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={4}>
            <HeadingInfoCard
              color="info"
              icon="people"
              title="入居者数"
              count={headingInfoData.residentCount}
              onClick={() => {
                handleSummaryClick(1)
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <HeadingInfoCard
              color="warning"
              icon="notificationsNone"
              title="不在検知件数"
              count={headingInfoData.detectionCount}
              onClick={() => {
                handleSummaryClick(2)
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <HeadingInfoCard
              color="primary"
              icon="reportProblem"
              title="機器エラー数"
              count={headingInfoData.alertCount}
              onClick={() => {
                handleSummaryClick(3)
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
      <MDBox mt={3}>
        <ResidentCardList
          residents={residentData}
          reloadData={handleReload}
          facilityId={id ? id : ""}
          deviceData={devices}
          devicePlace={devicePlaces}
          filter={filter}
        />
      </MDBox>
    </DashboardLayout>
  )
}

export default Facility
